import camelcaseKeys from 'camelcase-keys';
import type { ScaError } from './sca-errors';
import { hasProperty, isObject } from './object-helpers';

/**
 * Type guard for ResponseError-like objects
 */
interface ResponseErrorLike {
  status: number;
  data: unknown;
}

function isResponseErrorLike(error: unknown): error is ResponseErrorLike {
  return hasStatusProperty(error) && hasProperty(error, 'data');
}

function hasStatusProperty(obj: unknown): obj is { status: number } {
  return hasProperty(obj, 'status') && typeof obj.status === 'number';
}

function isScaErrorData(value: unknown): value is ScaError {
  return value === undefined || Array.isArray(value) || isObject(value);
}

function camelcaseScaErrorData(value: ScaError): ScaError {
  return value === undefined ? value : camelcaseKeys(value, { deep: true });
}

/**
 * Extracts SCA error data from either ResponseError and EmberData network errors.
 * Converts snake_case keys to camelCase if necessary.
 */
export function extractScaErrorData(error: unknown): {
  errorData: ScaError;
  status?: number;
} {
  if (isResponseErrorLike(error)) {
    // Handle ResponseError instances - extract data and convert to camelCase
    return {
      errorData: isScaErrorData(error.data) ? camelcaseScaErrorData(error.data) : undefined,
      status: error.status,
    };
  }

  // Handle regular errors - convert to camelCase if it's an object
  if (isScaErrorData(error)) {
    return {
      errorData: camelcaseScaErrorData(error),
      status: hasStatusProperty(error) ? error.status : undefined,
    };
  }

  // Unsupported payloads cannot contain an SCA challenge.
  return {
    errorData: undefined,
  };
}
